'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Ban, Check } from 'lucide-react'
import { TaskDialog } from '@/components/modals/task-dialog'
import { ConversationsRepository, type PeerMessagingState } from '@/core/repositories/conversations'

/** Both profile and conversation controls use the same account-pair API. */
export function PeerMessageBlockButton({ accountId, onStateChanged }: {
  accountId: string
  onStateChanged?: (state: PeerMessagingState | null) => void
}) {
  const [state, setState] = useState<PeerMessagingState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const changed = useRef(onStateChanged)
  changed.current = onStateChanged
  const generation = useRef(0)
  const inFlight = useRef(false)

  const load = useCallback(async () => {
    if (inFlight.current) return
    const request = ++generation.current
    setError(null)
    try {
      const next = await ConversationsRepository.peerMessagingState(accountId)
      if (request !== generation.current) return
      setState(next)
      changed.current?.(next)
    } catch {
      if (request !== generation.current) return
      setState(null)
      changed.current?.(null)
      setError('Could not load message settings.')
    }
  }, [accountId])

  useEffect(() => {
    setState(null)
    changed.current?.(null)
    void load()
    window.addEventListener('focus', load)
    return () => { ++generation.current; window.removeEventListener('focus', load) }
  }, [load])

  async function apply(blocked: boolean) {
    if (inFlight.current) return
    inFlight.current = true
    const request = ++generation.current
    setBusy(true)
    setError(null)
    try {
      const next = await ConversationsRepository.setPeerMessageBlock(accountId, blocked)
      if (request !== generation.current) return
      setState(next)
      changed.current?.(next)
      setConfirm(false)
    } catch {
      if (request !== generation.current) return
      setError(blocked ? 'Could not block messages. Try again.' : 'Could not unblock messages. Try again.')
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const label = state?.blockedByMe ? 'Unblock messages' : 'Block messages'
  return (
    <div>
      <button type="button" disabled={busy || (!state && !error)}
        className="flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-[var(--font_1)] hover:bg-[var(--background_2)] disabled:opacity-50"
        onClick={() => !state ? void load() : state.blockedByMe ? void apply(false) : setConfirm(true)}>
        {state?.blockedByMe ? <Check size={17} /> : <Ban size={17} />}
        {busy ? 'Saving…' : !state ? error ? 'Retry message settings' : 'Checking message settings…' : label}
      </button>
      {error && !confirm && <p role="alert" className="px-3 py-2 text-sm text-[var(--font_2)]">{error}</p>}
      <TaskDialog isOpen={confirm} title="Block messages?" busy={busy}
        description="Neither of you will be able to send messages to the other. This does not block bids or purchases. You can unblock messages from this profile or the conversation."
        onClose={() => setConfirm(false)}
        footer={<>
          <button type="button" disabled={busy} onClick={() => setConfirm(false)} className="min-h-11 rounded-lg px-4 text-[var(--font_1)]">Cancel</button>
          <button type="button" disabled={busy} onClick={() => void apply(true)} className="min-h-11 rounded-lg bg-[var(--cta)] px-4 !text-[var(--cta_text)]">{busy ? 'Saving…' : 'Block messages'}</button>
        </>}>
        {error && <p role="alert" className="text-sm text-[var(--font_2)]">{error}</p>}
      </TaskDialog>
    </div>
  )
}
